import wrapper from 'wrapper';
import 'whatwg-fetch';
import * as d3 from 'd3';
import { geoRobinson } from 'd3-geo-projection';
import * as topojson from 'topojson-client';
import { ResizeObserver as Polyfill } from '@juggle/resize-observer';
import events from '../../events-emitter';
import world from '../../assets/map/world_map_v2';
import disputedBlack from '../../assets/map/disputed_dotted_black_v2';
import disputedWhite from '../../assets/map/disputed_dotted_white';
import numFormat from './numberFormatter';

const ResizeObserver = window.ResizeObserver || Polyfill;

const vjCfg = wrapper.vjConfigObject ? wrapper.vjConfigObject : wrapper.vjConfigObj;
/* eslint-disable prefer-destructuring */
const assetsPath = vjCfg.assetsPath;
const currentLang = wrapper.config ? wrapper.config.language : vjCfg.language;

const virusData = `${assetsPath}/data/data_latest.json`;
// console.log('≈≈≈≈≈≈', 'virusData', virusData);

const container = wrapper.select('.coronavirus-map');

const isChinesePage = (wrapper.select('.bbc-news-vj-language--zhongwen_trad') || wrapper.select('.bbc-news-vj-language--zhongwen_simp'));
const isRTLPage = wrapper.select('.bbc-news-vj-direction--rtl');

const initialRegion = 'region_world';

const colours = {
    map: {
        confirmed: {
            fill: '#a1ccd9',
            stroke: '#4ba7bc',
            selected: {
                fill: '#4ba7bc',
                stroke: '#222',
            },
        },
        deaths: {
            fill: '#e8c5c5',
            stroke: '#d46666',
            selected: {
                fill: '#d46666',
                stroke: '#222',
            },
        },
        zhongwen: {
            deaths: {
                fill: '#d1c5e8',
                stroke: '#64469c',
                selected: {
                    fill: '#9b80ce',
                    stroke: '#222',
                },
            },
        },
    },
    bubbles: {
        confirmed: {
            fill: '#0b4d60',
            stroke: '#222',
        },
        deaths: {
            fill: '#990000',
            stroke: '#222',
        },
        zhongwen: {
            deaths: {
                fill: '#4D2C7A',
                stroke: '#222',
            },
        },
    },
};

let self;

// topojson data
const countries = topojson.feature(world, world.objects.bbc_world_map_code);
const disputedLinesBlack = topojson.feature(disputedBlack, disputedBlack.objects.disputed_dotted_black);
const disputedLinesWhite = topojson.feature(disputedWhite, disputedWhite.objects.disputed_dotted_white);

const mapRegions = {
    region_af: {
        center: [25.2251, 4.5260],
        scale: 240,
    },
    region_am_na: {
        center: [-95.2251, 35.5260],
        scale: 250,
    },
    region_am_lac: {
        center: [-780.2251, -10.5260],
        scale: 205,
    },
    region_as: {
        center: [90.2251, 20.5260],
        scale: 205,
    },
    region_me: {
        center: [52.2251, 29.5260],
        scale: 450,
    },
    region_oc: {
        center: [130, -13.5260],
        scale: 250,
    },
    region_eu: {
        center: [10.2251, 51.5260],
        scale: 570,
    },
    region_world: {
        center: [15, 20],
        scale: null, // we calculate it for world..
    },
};

class Map {
    constructor(regionId = 'region_world') {
        self = this;
        // used a holder for when window size updates
        this.regionId = regionId;
        this.dataKey = 'confirmed';
        this.worldLargestBubbleConfirmedValue = undefined;
        this.currentCode = null;
        this.tooltipTimeout = null;

        this.initMap();

        // add events
        events.on('updated-dropdown', (evt) => {
            this.regionId = evt.regionId;
            this.updateRegion(evt);
            wrapper.select(`.summary--${this.dataKey}`).focus();
        });
        events.on('updated-buttons', (evt) => {
            const e = evt;
            evt.stopPropagation();
            this.dataKey = evt.dataKey;
            e.regionId = this.regionId;
            // console.log('this.dataKey', this.dataKey);
            this.updateRegion(e);
            container.focus();
        });
        // listen to size changes on container
        const ro = new ResizeObserver(el => {
            this.updateRegion({ regionId: this.regionId });
        });
        ro.observe(container);
    }

    getDataValues(data, key) {
        // get all confirmed numbers into an array
        const dataValues = [];
        Object.values(data).forEach(row => {
            if (row.countries) Object.values(row.countries).map(obj => dataValues.push(obj[key]));
            return true;
        });
        return dataValues;
    }

    adjustDropdownWidth() {
        const dropdownLabelWidth = wrapper.select('.input-container label').getBoundingClientRect().width;
        wrapper.select('.regions-dropdown').style.width = `calc(98% - ${dropdownLabelWidth}px)`;
        wrapper.select('.summary--label').style.minWidth = `${dropdownLabelWidth}px`;
        wrapper.select('.summary--confirmed').style.right = `-${dropdownLabelWidth}px`;
        if (isRTLPage) {
            wrapper.select('.summary--confirmed').style.right = `${dropdownLabelWidth - 32}px`;
        }
    }

    getData() {
        if (!this.dataPromise) {
            this.dataPromise = d3.json(virusData);
        }
        return this.dataPromise;
    }

    initMap() {
        const { width } = container.getBoundingClientRect();
        const height = width / 2;
        const svg = d3.select(container).append('svg');
        const regionId = 'region_world';

        this.adjustDropdownWidth();
        // console.log(`SVG height x width = ${height}x${width}`);

        // get virus data, then build map
        this.getData().then((jsonData) => {
            // d3 like to go over arrays of objects:
            // sp lets get all the data we need into an array of objects
            const worldBubbleData = [];
            Object.values(jsonData.regions).forEach(row => {
                if (row.countries) {
                    const countryCodes = Object.keys(row.countries);
                    Object.values(row.countries).map((obj, i) => {
                        const code = countryCodes[i];
                        const lat = obj.latLong.split(',')[0];
                        const long = obj.latLong.split(' ')[1];
                        return worldBubbleData.push({
                            name: __(code),
                            code: code,
                            confirmed: obj.confirmed,
                            deaths: obj.deaths,
                            lat: lat,
                            long: long,
                        });
                    });
                }
                return true;
            });

            this.worldBubbleData = worldBubbleData;
            // console.log('this.worldBubbleData', { data: this.worldBubbleData });

            const dataValues = this.getDataValues(jsonData.regions, 'confirmed');
            const deathsValues = this.getDataValues(jsonData.regions, 'deaths');

            // console.log('world dataValues', dataValues);

            const valueDomain = d3.extent(dataValues);
            const deathsValueDomain = d3.extent(deathsValues);

            // console.log('world valueDomain', valueDomain);

            const getWorldScale = d3.scaleLinear().domain([320, 560, 1024]).range([50, 90, 150]);
            let worldScale = getWorldScale(width);
            const worldCenter = mapRegions.region_world.center;

            // adjust the scale/zoom for full width pages, mobile, etc
            if (width >= 940) { // full width page, no RHS column
                worldScale = getWorldScale(width * 1.2);
            }
            else if (width <= 440) { // mobiles
                worldScale = getWorldScale(width * 1.1);
            }

            // console.log('worldScale', worldScale);

            // set the initial view (scale/center)
            const myProjection = geoRobinson()
                .center(worldCenter)
                .scale(worldScale)
                .translate([width / 2, height / 2]);

            const geoPath = d3.geoPath()
                .projection(myProjection);

            let currentStrokeColour = colours.map[this.dataKey].stroke;
            let currentFillColour = colours.map[this.dataKey].fill;
            let currentBubbleColour = colours.bubbles[this.dataKey].fill;
            this.highlightedFillColour = colours.map[this.dataKey].selected.fill;
            this.highlightedStrokeColour = colours.map[this.dataKey].selected.stroke;
            // modify death colours for chinese audience
            if (this.dataKey === 'deaths' && isChinesePage) {
                currentStrokeColour = colours.map.zhongwen[this.dataKey].stroke;
                currentFillColour = colours.map.zhongwen[this.dataKey].fill;
                currentBubbleColour = colours.bubbles.zhongwen[this.dataKey].fill;
                this.highlightedFillColour = colours.map.zhongwen[this.dataKey].selected.fill;
                this.highlightedStrokeColour = colours.map.zhongwen[this.dataKey].selected.stroke;
            }
            this.currentBubbleColour = currentBubbleColour;

            const scaleStrokeColour = d3.scaleOrdinal()
                .domain(['None', 'Coloured'])
                .range(['#777777', currentStrokeColour]);

            const scaleFillColour = d3.scaleOrdinal()
                .domain(['None', 'Coloured'])
                .range(['#e4e4e4', currentFillColour]);

            const scaleMaxRadius = d3.scaleLinear().domain([50, 90, 200]).range([12, 20, 40]);
            const scaleMinRadius = d3.scaleLinear().domain([50, 90, 200]).range([0.5, 0.5, 1]);


            // console.log('world scaleMinRadius', scaleMinRadius);
            // console.log('world scaleMaxRadius', scaleMaxRadius);

            const circleScaleAdjustment = this.adjustCircleScaling(regionId);

            // console.log('world circleScaleAdjustment', circleScaleAdjustment);

            const maxRadius = scaleMaxRadius(myProjection.scale() / circleScaleAdjustment);
            const minRadius = scaleMinRadius(worldScale / circleScaleAdjustment);

            // console.log('world maxRadius', maxRadius);
            // console.log('world minRadius', minRadius);

            const scaleRadius = d3.scaleSqrt()
                .domain(valueDomain)
                .range([minRadius, maxRadius]);

            this.worldScale = worldScale;
            this.worldMaxRadius = maxRadius;
            this.worldMinRadius = minRadius;
            /* eslint-disable prefer-destructuring */
            this.worldLargestBubbleConfirmedValue = valueDomain[1];
            this.worldLargestBubbleDeathsValue = deathsValueDomain[1];

            const colorState = jsonData.regions[initialRegion][this.dataKey] > 0 ? 'Coloured' : 'None';

            //
            // render svg
            //
            svg
                .attr('width', width)
                .attr('height', height);

            const plotWorld = svg.append('g')
                .attr('class', 'world_map');

            const plotDisputed = svg.append('g')
                .attr('class', 'disputed');

            const plotBubbles = svg.append('g')
                .attr('class', 'bubbles');

            plotWorld.selectAll('.country')
                .data(countries.features) // geometry for the countries
                .enter()
                .append('path')
                .attr('class', 'country')
                .attr('dataset', d => d.properties.code)
                .attr('d', geoPath)
                .style('stroke', d => scaleStrokeColour(colorState))
                .style('fill', d => scaleFillColour(colorState))
                .style('stroke-width', 0.5)
                .style('stroke-linejoin', 'round')
                .on('mouseover', (d) => {
                    // console.log(this, d3.select(this));
                    // disable country highlighting, cos of disputed borders
                    // const elems = wrapper.selectAll(`.coronavirus-map path[dataset="${d.properties.code}"]`);
                    // // console.log('elems', elems);
                    // elems.forEach(el => {
                    //     // console.log('d3 el => ', d3.select(el));
                    //     d3.select(el).style('fill', this.highlightedFillColour);
                    //     d3.select(el).style('stroke', this.highlightedStrokeColour);
                    // });
                    self.showTooltip(d.properties.code, regionId);
                })
                .on('mouseout', () => {
                    // disable country highlighting, cos of disputed borders
                    // const elems = wrapper.selectAll(`.coronavirus-map path[dataset="${d.properties.code}"]`);
                    // elems.forEach(el => {
                    //     // console.log('d3 el => ', d3.select(el));
                    //     d3.select(el).style('fill', currentFillColour);
                    //     d3.select(el).style('stroke', currentStrokeColour);
                    // });
                    self.hideTooltipOnTimeout();
                });


            // disputed lines BLACK
            plotDisputed.selectAll('.black_lines')
                .data(disputedLinesBlack.features) // geometry for the lines
                .enter()
                .append('path')
                .attr('class', 'black_lines')
                .attr('d', d => geoPath(d.geometry))
                .style('fill', 'none')
                .style('stroke', d => scaleStrokeColour(colorState))
                .style('stroke', d => {
                    if (d.properties['For data p'] === 'Ukraine') {
                        return scaleStrokeColour(colorState);
                    }
                    return scaleFillColour(colorState);
                })
                .style('stroke-linejoin', 'round')
                .style('stroke-width', 1)
                .style('stroke-dasharray', 2);

            // disputed lines WHITE
            plotDisputed.selectAll('.white_lines')
                .data(disputedLinesWhite.features) // geometry for the lines
                .enter()
                .append('path')
                .attr('class', 'white_lines')
                .attr('d', d => geoPath(d.geometry))
                .style('fill', 'none')
                .style('stroke', d => scaleFillColour(colorState))
                .style('stroke', d => {
                    if (d.properties['For data p'] === 'Ukraine') {
                        return scaleStrokeColour(colorState);
                    }
                    return scaleFillColour(colorState);
                })
                .style('stroke-width', 1)
                .style('stroke-dasharray', 2)
                .style('stroke-linejoin', 'round');

            // add the bubbles to the map
            plotBubbles.selectAll('.bubbles')
                .data(worldBubbleData)
                .enter()
                .append('circle')
                .on('mouseover', (d) => {
                    self.showTooltip(d.code, regionId);
                })
                .on('mouseout', () => {
                    self.hideTooltipOnTimeout();
                })
                .attr('class', d => `bubble ${d.code}`)
                .attr('cx', d => myProjection([d.long, d.lat])[0])
                .attr('cy', d => myProjection([d.long, d.lat])[1])
                .attr('r', d => scaleRadius(d[this.dataKey]))
                .style('stroke', '#222')
                .style('fill', colours.bubbles[this.dataKey])
                .style('fill-opacity', 0.6);

            // add map to page
            wrapper.select('.coronavirus-map').innerHTML = '';
            wrapper.select('.coronavirus-map').appendChild(svg.node());
            this.createTooltip();

            // update header text
            const totalConfirmed = jsonData.regions[initialRegion].confirmed;
            const totalDeaths = jsonData.regions[initialRegion].deaths;
            const totalRecoveries = jsonData.regions[initialRegion].recoveries;
            this.updateSummaryText(totalConfirmed, totalDeaths, totalRecoveries);

            // console.log('totalConfirmed', totalConfirmed);
            // console.log('totalDeaths', totalDeaths);
            // console.log('totalRecoveries', totalRecoveries);

            const allBubbleSizes = wrapper.selectAll('.coronavirus-map circle.bubble').map(circle => circle.getBoundingClientRect().width);

            // console.log('allBubbleSizes', allBubbleSizes);

            this.widthOfWorldsLargestBubble = Math.max.apply(this, allBubbleSizes); // ...sometimes returns Infinity in IE :/

            // console.log('this.widthOfWorldsLargestBubble', this.widthOfWorldsLargestBubble);

            // update circle size in legend
            this.updateLegend(regionId);
        });
    }

    createTooltip() {
        this.tt = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        this.tt.setAttribute('class', 'tt');
        this.tt.setAttribute('width', 150);
        this.tt.setAttribute('height', 80);
    }

    hideTooltip() {
        this.currentCode = null;
        this.tt.style.opacity = 0;
        this.tt.style.width = '0px';
        this.tt.style.height = '0px';
        self.rmBubbleHighlight();
    }

    hideTooltipOnTimeout() {
        this.tooltipTimeout = setTimeout(self.hideTooltip.bind(this), 50);
    }

    getCountryData(code, regionId) {
        const currentData = regionId === 'region_world' ? this.worldBubbleData : this.regionBubbleData;
        const data = currentData.filter(country => {
            return country.code === code ? country : '';
        });
        return data[0];
    }

    showTooltip(code, regionId) {
        clearTimeout(this.tooltipTimeout);
        if (this.currentCode === code) {
            return;
        }
        this.tt.remove();
        this.adjustDropdownWidth();
        const currentCircle = wrapper.select(`circle.${code}`);
        const containerDimensions = container.getBoundingClientRect();
        if (currentCircle) {
            this.createTooltip();
            this.vocabKey = this.dataKey === 'deaths' ? __('deaths') : __('confirmed');

            let offsetX = 10;
            let offsetY = 10;
            if (+currentCircle.getAttribute('cy') > (containerDimensions.height / 2)) offsetY = -80;
            if (+currentCircle.getAttribute('cx') > (containerDimensions.width / 2)) offsetX = -160;

            const countryData = this.getCountryData(code, regionId);
            const currentCircleCy = +currentCircle.getAttribute('cy') + offsetY;
            const currentCircleCx = +currentCircle.getAttribute('cx') + offsetX;
            const textX = isRTLPage ? '95%' : '8';

            const mainValue = numFormat.format(currentLang, countryData[this.dataKey]);
            let ttTextValue = `${mainValue} ${this.vocabKey}`;
            if (isRTLPage) ttTextValue = `${this.vocabKey} ${mainValue}`;

            this.tt.innerHTML = `
            <svg class="tt-svg" width="160" height="52" x="${+currentCircleCx}" y="${+currentCircleCy}">
                <g>
                    <rect width="100%" height="100%"></rect>
                    <text class="tt-country-name" x="${textX}" y="20" fill="#3F3F42">${countryData.name}</text>
                    <text class="tt-country-value" x="${textX}" y="42" fill="${this.currentBubbleColour}" font-weight="bold">
                    ${ttTextValue}
                    </text>
                </g>
            </svg>`;

            wrapper.select('.coronavirus-map svg').appendChild(this.tt);
            const nameWidth = wrapper.select('.tt-country-name').getBoundingClientRect().width;
            const valueWidth = wrapper.select('.tt-country-value').getBoundingClientRect().width;
            const textWidth = nameWidth > valueWidth ? nameWidth : valueWidth;
            wrapper.select('.tt-svg').setAttribute('width', textWidth + 16);
            this.currentCode = code;
            this.addBubbleHighlight(code);
        }
    }

    addBubbleHighlight(code) {
        this.rmBubbleHighlight();
        wrapper.select(`circle.bubble.${code}`).classList.add('highlighted-bubble');
    }

    rmBubbleHighlight() {
        [].slice.call(wrapper.selectAll('circle')).forEach(el => el.classList.remove('highlighted-bubble'));
    }

    adjustCircleScaling(regionId) {
        const width = container.getBoundingClientRect().width;
        let circleScaleAdjustment = 1; // 1 = 100%, i.e, same size

        if (width < 600) {
            circleScaleAdjustment = 2;
        }
        else if (width < 480) {
            circleScaleAdjustment = 4;
        }
        else if (width < 400) {
            circleScaleAdjustment = 8;
        }

        return circleScaleAdjustment;
    }

    setFocusedButton() {
        if (this.dataKey === 'confirmed') {
            wrapper.select('.summary--confirmed').classList.add('summary--confirmed-in-focus');
            wrapper.select('.summary--deaths').classList.remove('summary--deaths-in-focus');
        }
        if (this.dataKey === 'deaths') {
            wrapper.select('.summary--deaths').classList.add('summary--deaths-in-focus');
            wrapper.select('.summary--confirmed').classList.remove('summary--confirmed-in-focus');
        }
    }

    updateRegion(evt) {
        /* eslint-disable no-param-reassign */
        if (!evt.dataKey) evt.dataKey = this.dataKey;
        this.getData().then((jsonData) => {
            // get values of each region
            Object.values(jsonData.regions).filter((region, i) => {
                // get region key
                const regionId = Object.keys(jsonData.regions)[i];

                // if region key matches region chosen in dropdown
                if (regionId === evt.regionId) {
                    const width = container.getBoundingClientRect().width;

                    let height = width / 1.4; // make map taller on mobile, by default
                    // make map even taller on small portait phones
                    if (width <= 340 && window.innerHeight > window.innerWidth) {
                        height = width / 1.1;
                    }
                    // ..except for world view - reduce height
                    if (regionId === 'region_world') height = width / 2;

                    const getWorldScale = d3.scaleLinear().domain([320, 560, 1024]).range([50, 90, 150]);
                    const worldScale = getWorldScale(width);

                    // newScale is the new scale/bounds of the region
                    let newScale = mapRegions[regionId].scale ? mapRegions[regionId].scale : worldScale;
                    const newCenter = mapRegions[regionId].center;

                    // adjust the region scale/zoom for full width pages, mobile, etc:
                    if (width >= 940) { // no RHS column
                        // zoom in a bit more than usual
                        newScale = getWorldScale(width * 1.2);
                        if (regionId !== 'region_world') {
                            newScale = mapRegions[regionId].scale * 1.4;
                        }
                    }
                    else if (width <= 440) { // mobiles
                        newScale = getWorldScale(width * 1.1);
                        if (regionId !== 'region_world') {
                            newScale = mapRegions[regionId].scale / 1.65;
                        }
                    }
                    else if (width <= 340) { // smaller mobiles
                        if (regionId !== 'region_world') {
                            newScale = mapRegions[regionId].scale / 2;
                        }
                    }

                    const newProjection = geoRobinson()
                        .center(newCenter)
                        .scale(newScale)
                        .translate([width / 2, height / 2]);

                    const geoPath = d3.geoPath()
                        .projection(newProjection);

                    // update the svg
                    const svg = d3.select(wrapper.select('.coronavirus-map svg'));

                    svg
                        .attr('height', height)
                        .attr('width', width);

                    // remove data from previous region
                    svg.selectAll('.country').remove();
                    svg.selectAll('.disputed').remove();
                    svg.selectAll('.bubbles').remove();

                    const plotWorld = svg.selectAll('.world_map');

                    const plotDisputed = svg.append('g')
                        .attr('class', 'disputed');

                    const plotBubbles = svg.append('g')
                        .attr('class', 'bubbles');

                    let currentStrokeColour = colours.map[this.dataKey].stroke;
                    let currentFillColour = colours.map[this.dataKey].fill;
                    let highlightedFillColour = colours.map[this.dataKey].selected.fill;
                    let highlightedStrokeColour = colours.map[this.dataKey].selected.stroke;
                    let currentBubbleColour = colours.bubbles[this.dataKey].fill;
                    // modify death colours for chinese audience
                    if (this.dataKey === 'deaths' && isChinesePage) {
                        currentStrokeColour = colours.map.zhongwen[this.dataKey].stroke;
                        currentFillColour = colours.map.zhongwen[this.dataKey].fill;
                        highlightedFillColour = colours.map.zhongwen[this.dataKey].selected.fill;
                        highlightedStrokeColour = colours.map.zhongwen[this.dataKey].selected.stroke;
                        currentBubbleColour = colours.bubbles.zhongwen[this.dataKey].fill;
                    }
                    this.currentBubbleColour = currentBubbleColour;

                    this.regionBubbleData = [];
                    // d3 like to go over arrays of objects:
                    // so lets get all the data we need into an array of objects
                    Object.entries(Object.entries(jsonData.regions[regionId])[2][1]).forEach(country => {
                        const lat = country[1].latLong.split(',')[0];
                        const long = country[1].latLong.split(' ')[1];
                        const data = {
                            code: country[0],
                            name: __(country[0]),
                            region: regionId,
                            confirmed: country[1].confirmed,
                            deaths: country[1].deaths,
                            lat: lat,
                            long: long,
                        };
                        return this.regionBubbleData.push(data);
                    });

                    // console.log('ΩΩΩΩΩΩΩΩΩ this.worldBubbleData', this.worldBubbleData);
                    // console.log('ΩΩΩΩΩΩΩΩΩ this.regionBubbleData', this.regionBubbleData);
                    const bubbleData = regionId === 'region_world' ? this.worldBubbleData : this.regionBubbleData;

                    const colorState = (d) => {
                        let retVal;
                        // world region
                        if (d && d.properties && d.properties.code === 'false') {
                            retVal = jsonData.regions[regionId][this.dataKey] > 0 ? 'Coloured' : 'None';
                        }
                        // all other regions
                        else if (d && d.properties && d.properties.code !== 'false') {
                            retVal = 'Coloured';
                        }
                        return retVal;
                    };

                    const scaleStrokeColour = d3.scaleOrdinal()
                        .domain(['None', 'Coloured'])
                        .range(['#777777', currentStrokeColour]);

                    const scaleFillColour = d3.scaleOrdinal()
                        .domain(['None', 'Coloured'])
                        .range(['#e4e4e4', currentFillColour]);

                    plotWorld
                        .selectAll('.country')
                        .data(countries.features) // geometry for the countries
                        .enter()
                        .append('path')
                        .attr('class', 'country')
                        .attr('dataset', d => d.properties.code)
                        .attr('d', geoPath)
                        .style('stroke', d => scaleStrokeColour(colorState(d)))
                        .style('fill', d => scaleFillColour(colorState(d)))
                        .style('stroke-width', 0.5)
                        .style('stroke-linejoin', 'round')
                        .on('mouseover', (d) => {
                            // console.log(this, d3.select(this));
                            // disable country highlighting, cos of disputed borders
                            // const elems = wrapper.selectAll(`.coronavirus-map path[dataset="${d.properties.code}"]`);
                            // console.log('elems', elems);
                            // elems.forEach(el => {
                            //     console.log('d3 el => ', d3.select(el));
                            //     d3.select(el).style('fill', highlightedFillColour);
                            //     d3.select(el).style('stroke', highlightedStrokeColour);
                            // });
                            self.showTooltip(d.properties.code, regionId);
                        })
                        .on('mouseout', () => {
                            // console.log(this, d3.select(this));
                            // disable country highlighting, cos of disputed borders
                            // const elems = wrapper.selectAll(`.coronavirus-map path[dataset="${d.properties.code}"]`);
                            // elems.forEach(el => {
                            //     console.log('d3 el => ', d3.select(el));
                            //     d3.select(el).style('fill', currentFillColour);
                            //     d3.select(el).style('stroke', currentStrokeColour);
                            // });
                            self.hideTooltipOnTimeout();
                        });

                    // disputed lines BLACK
                    plotDisputed
                        .selectAll('.black_lines')
                        .data(disputedLinesBlack.features) // geometry for the lines
                        .enter()
                        .append('path')
                        .attr('class', 'black_lines')
                        .attr('d', d => geoPath(d.geometry))
                        .style('fill', 'none')
                        .style('stroke', d => scaleStrokeColour(colorState(d)))
                        .style('stroke-linejoin', 'round')
                        .style('stroke-width', 1)
                        .style('stroke-dasharray', 2);

                    // disputed lines WHITE
                    plotDisputed
                        .selectAll('.white_lines')
                        .data(disputedLinesWhite.features) // geometry for the lines
                        .enter()
                        .append('path')
                        .attr('class', 'white_lines')
                        .attr('d', d => geoPath(d.geometry))
                        .style('fill', 'none')
                        .style('stroke', d => scaleFillColour(colorState(d)))
                        .style('stroke-width', 1)
                        .style('stroke-dasharray', 2)
                        .style('stroke-linejoin', 'round');

                    const scaleMaxRadius = d3.scaleLinear().domain([50, 90, 200]).range([12, 20, 40]);
                    const scaleMinRadius = d3.scaleLinear().domain([50, 90, 200]).range([0.5, 0.5, 1]);

                    // responsive circle scaling for screen sizes
                    const circleScaleAdjustment = this.adjustCircleScaling(regionId);

                    // ...if we want to make the circle sizes relative to the other circles
                    // in the REGION (not whole world), use these max/min scales instead of
                    // this.worldMinRadius and this.worldMaxRadius ...
                    // const maxRadius = scaleMaxRadius(newProjection.scale() / circleScaleAdjustment);
                    // const minRadius = scaleMinRadius(newScale / circleScaleAdjustment);

                    let adjustedMinRadius = this.worldMinRadius;
                    let adjustedMaxRadius = this.worldMaxRadius;

                    // make all circles a bit bigger for region views (not world)
                    if (regionId !== 'region_world') {
                        adjustedMinRadius = (this.worldMinRadius * 2) * circleScaleAdjustment;
                        adjustedMaxRadius = (this.worldMaxRadius * 2) * circleScaleAdjustment;
                    }

                    // console.log('this.dataKey', this.dataKey);
                    // console.log('this.worldBubbleData', this.worldBubbleData);

                    const dataValues = [];
                    Object.values(this.worldBubbleData).forEach(country => {
                        dataValues.push(country[this.dataKey]);
                        return true;
                    });
                    const confirmedValues = [];
                    Object.values(this.worldBubbleData).forEach(country => {
                        confirmedValues.push(country.confirmed);
                        return true;
                    });

                    const confirmedValueDomain = d3.extent(confirmedValues);
                    const dataValueDomain = d3.extent(dataValues);

                    // console.log('!!!!!!!!! this.worldMaxRadius', this.worldMaxRadius);

                    const scaleRadius = d3.scaleSqrt()
                        .domain(confirmedValueDomain)
                        // .range([minRadius, this.worldMaxRadius]);
                        .range([adjustedMinRadius, adjustedMaxRadius]);

                    // console.log('newScale, minRadius, maxRadius, this.worldMinRadius, this.worldMaxRadius', newScale, this.worldMinRadius, this.worldMaxRadius);

                    plotBubbles.selectAll('.bubbles')
                        .data(bubbleData)
                        .enter()
                        .append('circle')
                        .on('mouseover', (d) => {
                            self.showTooltip(d.code, regionId);
                        })
                        .on('mouseout', () => {
                            self.hideTooltipOnTimeout();
                        })
                        .attr('class', d => (d[this.dataKey] === 0 ? `bubble ${d.code} hidden` : `bubble ${d.code}`))
                        .attr('cx', d => newProjection([d.long, d.lat])[0])
                        .attr('cy', d => newProjection([d.long, d.lat])[1])
                        .attr('r', d => scaleRadius(d[this.dataKey]))
                        .style('stroke', '#222')
                        .style('fill', currentBubbleColour)
                        .style('fill-opacity', 0.6)
                        .style('pointer-events', 'fill');

                    const allBubbleSizes = wrapper.selectAll('.coronavirus-map circle.bubble').map(circle => circle.getBoundingClientRect().width);
                    this.widthOfWorldsLargestBubble = Math.max.apply(this, allBubbleSizes); // ...sometimes returns Infinity in IE :/

                    // update header text
                    const totalConfirmed = jsonData.regions[regionId].confirmed;
                    const totalDeaths = jsonData.regions[regionId].deaths;
                    const totalRecoveries = jsonData.regions[regionId].recoveries;
                    this.updateSummaryText(totalConfirmed, totalDeaths, totalRecoveries);

                    // update legend
                    const lowValue = this.dataKey === 'confirmed' ? confirmedValueDomain[0] : dataValueDomain[0];
                    const highValue = this.dataKey === 'confirmed' ? confirmedValueDomain[1] : dataValueDomain[1];
                    this.updateLegend(regionId, lowValue, highValue, adjustedMinRadius, adjustedMaxRadius);
                }

                return evt.regionId; // stop linter moaning
            });
        });
    }

    updateSummaryText(confirmed, deaths, recoveries) {
        // update the header text
        wrapper
            .select('.summary--confirmed .summary--number')
            .innerText = numFormat.format(currentLang, confirmed);

        wrapper
            .select('.summary--deaths .summary--number')
            .innerText = numFormat.format(currentLang, deaths);

        // wrapper
        //     .select('.summary--recoveries .summary--number')
        //     .innerText = numFormat.format(currentLang, recoveries);

        this.setFocusedButton();
    }

    updateLegend(regionId, lowValue, highValue, minRadius, maxRadius) {
        let minR;
        let maxR;
        const pi = 3.14159265359;

        if (this.dataKey === 'confirmed') {
            minR = minRadius;
            maxR = maxRadius;
        }
        else if (this.dataKey === 'deaths') {
            minR = minRadius;
            maxR = maxRadius / pi;
        }
        // get the scale for the highest/lowest values for world
        const scaleRadius = d3.scaleSqrt()
            .domain([lowValue, highValue / pi])
            .range([minR, maxR]);

        const highestBubbleValue = this.dataKey === 'confirmed' ? this.worldLargestBubbleConfirmedValue : this.worldLargestBubbleDeathsValue;

        const roundTo = this.dataKey === 'confirmed' ? 100000 : 10000;
        const roundedMax = Math.round(highestBubbleValue / roundTo) * roundTo;
        const roundedMed = Math.round((highestBubbleValue / 2) / roundTo) * roundTo;
        const roundedMin = Math.round((highestBubbleValue / 10) / (roundTo / 10)) * (roundTo / 10);
        // scale the radius of the circle to the largest value in the world
        const radius = scaleRadius(roundedMax);
        const radiusMid = scaleRadius(roundedMed);
        const radiusMin = scaleRadius(roundedMin);
        // console.log('radius, radiusMid, radiusMin', radius, radiusMid, radiusMin);
        let bubbleWidth = radius;
        const bubbleWidthMid = radiusMid;
        const bubbleWidthMin = radiusMin;
        // console.log('bubbleWidth, bubbleWidthMid, bubbleWidthMin', bubbleWidth, bubbleWidthMid, bubbleWidthMin);

        // if world, bubble width should simply match width of largest bubble
        if (regionId === 'region_world') {
            bubbleWidth = this.widthOfWorldsLargestBubble;
        }

        let labelAdjustment;
        const containerWidth = container.getBoundingClientRect().width;
        if (containerWidth < 340) {
            labelAdjustment = 1.75;
        }
        else if (containerWidth < 400) {
            labelAdjustment = 1.45;
        }
        else if (containerWidth < 600) {
            labelAdjustment = 0.9;
        }
        else if (containerWidth > 600) {
            labelAdjustment = 0.9;
        }
        else if (containerWidth > 800) {
            labelAdjustment = 1;
        }

        // console.log('legend:  lowValue, highValue', `${lowValue}, ${highValue}`);
        // console.log('legend:  minRadius, maxRadius', minRadius, maxRadius);
        // console.log('legend:  highestBubbleValue', highestBubbleValue);
        // console.log('legend:  legend bubble width', bubbleWidth);

        const legend = wrapper.select('.coronavirus-map-legend');
        const legendClasses = this.dataKey === 'deaths' ? 'legend-circle-deaths' : '';
        let otherCircles = '';

        if (regionId !== 'region_world') {
            otherCircles = `
                <!--
                <div class="legend-circle legend-circle-medium ${legendClasses}" style="width: ${bubbleWidthMid}px; height: ${bubbleWidthMid}px; left: calc(50% - ${bubbleWidthMid}px / 2);">
                    <span class="legend-value" >${numFormat.format(currentLang, roundedMed)}</span>
                </div>
                -->
                <div class="legend-circle legend-circle-small ${legendClasses}"  style="width: ${bubbleWidthMin}px; height: ${bubbleWidthMin}px; left: calc(50% - ${bubbleWidthMin}px / 2);">
                    <span class="legend-value" style="${this.dataKey === 'deaths' ? `top: calc(-${bubbleWidthMin}px * ${labelAdjustment})` : ''}">${numFormat.format(currentLang, roundedMin)}</span>
                </div>`;
        }

        legend.innerHTML = `
            <div class="legend-container ${regionId === 'region_world' ? 'world' : ''}" style="min-height: ${bubbleWidth}px;">
                <div class="legend-circle legend-circle-large ${legendClasses}" style="width: ${bubbleWidth}px; height: ${bubbleWidth}px; left: calc(50% - ${bubbleWidth}px / 2);">
                    <span class="legend-value" >${numFormat.format(currentLang, roundedMax)}</span>
                </div>
                ${otherCircles}
            </div>`;

        wrapper.select('.coronavirus-map-legend-note').innerText = this.dataKey === 'confirmed' ? __('bubble-note') : __('bubble-note-deaths');
    }
}

export default Map;
